import React, { useState, useEffect } from "react";

// Settings
import { BreakPoints } from "../DesignSettings";

// Dependencies
import { Link, Redirect } from "react-router-dom";
import { mixpanelToken } from "../settings";
import backgroundImg from "../static/medical-icons-background.jpg";

// Styles
import styled from "styled-components";
import {
  Title,
  MediumText,
  SmallText,
  BodyCopy,
  SmallTitle,
} from "../Typography";

import { Line } from "../Elements";
import Spinner from "../Utilities/Spinner";
import Button from "../Utilities/Button";

import { fontFamily, yellow } from "../Cytoplasm";

import { useLogin } from "./useAuth";
import { redirectToApp } from "../auth";

const mixpanel = require("mixpanel-browser");
// Enabling the debug mode flag is useful during implementation,
// but it's recommended you remove it for production
mixpanel.init(mixpanelToken);

export default function Login(props) {
  const [username, setUsername] = useState("");
  function handleUsername(e) {
    setUsername(e.target.value);
    handleButtonActive();
  }
  const [password, setPassword] = useState("");
  function handlePassword(e) {
    setPassword(e.target.value);
    handleButtonActive();
  }

  useEffect(() => {
    if (username.length > 0 && password.length > 0) {
      setFormFilled(true);
    }
  }, [username, password]);

  const [formFilled, setFormFilled] = useState(false);
  function handleButtonActive() {
    if (username.length > 0 && password.length > 0) {
      setFormFilled(true);
    }
  }

  function handleLogin(e, login) {
    e.preventDefault();
    if (username.length > 0 && password.length > 0) {
      login();
    }
  }

  const [formSubmitted, setFormSubmitted] = useState(false);
  function handleSubmit(e, login) {
    e.preventDefault();

    if (username.length > 0 && password.length > 0) {
      login();
      setFormSubmitted(true);
    }
  }
  const [cannotAuthMsg, setCannotAuthMsg] = useState();
  function cannotAuth(msg) {
    setCannotAuthMsg(msg);
  }

  const [login, { data, loading, error }] = useLogin({
    variables: { username: username, password: password },
    onCompleted: (data) => {
      if (!data.login.user) {
        cannotAuth(
          "The username and/or password do not match. Please try again."
        );
      } else {
        if (props.match.params.redirect) {
          switch (props.match.params.redirect) {
            case "rounds":
              redirectToApp("https://rounds.medzcool.com");
              break;
            case "lab":
              redirectToApp("https://lab.medzcool.com");
              break;
            case "kardiograph":
              redirectToApp("https://kardiograph.medzcool.com");
              break;
            case "kardiograph-staging":
              redirectToApp("https://kardiograph-staging.medzcool.com");
              break;
            case "auscultate-staging":
              redirectToApp("https://auscultate-staging.medzcool.com");
              break;
            case "manuscript":
              redirectToApp("https://manuscript.medzcool.com");
              break;
            case "dashboard":
              redirectToApp("https://dashboard.medzcool.com");
              break;
            case "auscultate":
              props.history.push(
                "/" + props.match.params.redirect + "/confirm-purchase"
              );
              break;
            case "cranial":
              props.history.push(
                "/" + props.match.params.redirect + "/confirm-purchase"
              );
              break;
            case "clinic":
              props.history.push("/" + props.match.params.redirect);
              break;
          }
        } else {
          mixpanel.identify(data.login.user.id);

          mixpanel.people.set({
            $email: data.login.user.email,
          });

          mixpanel.track("Login", {
            Location: "Isolated Login",
          });
          props.history.push("/");
        }
      }
    },
  });

  // Silent check (?silent=1): an app with optional sign-in asks whether the
  // visitor is already signed in here. Signed in: the branch below hands off
  // with a code as usual. Signed out: go straight back, without a login form.
  const silentApps = {
    kardiograph: "https://kardiograph.medzcool.com",
    "kardiograph-staging": "https://kardiograph-staging.medzcool.com",
  };
  const silent =
    new URLSearchParams(props.location.search).get("silent") === "1" &&
    silentApps[props.match.params.redirect];
  if (silent && !props.user) {
    window.location.replace(`${silent}/?signed_out=1`);
    return null;
  }

  if (props.user) {
    // Already signed in here: hand the login to the app that sent us.
    if (props.match.params.redirect === "rounds") {
      redirectToApp("https://rounds.medzcool.com");
      return null;
    }
    if (props.match.params.redirect === "lab") {
      redirectToApp("https://lab.medzcool.com");
      return null;
    }
    if (props.match.params.redirect === "kardiograph") {
      redirectToApp("https://kardiograph.medzcool.com");
      return null;
    }
    if (props.match.params.redirect === "kardiograph-staging") {
      redirectToApp("https://kardiograph-staging.medzcool.com");
      return null;
    }
    if (props.match.params.redirect === "auscultate-staging") {
      redirectToApp("https://auscultate-staging.medzcool.com");
      return null;
    }
    if (props.match.params.redirect === "manuscript") {
      redirectToApp("https://manuscript.medzcool.com");
      return null;
    }
    if (props.match.params.redirect === "dashboard") {
      redirectToApp("https://dashboard.medzcool.com");
      return null;
    }
    if (
      props.match.params.redirect === "cranial" ||
      props.match.params.redirect === "auscultate"
    ) {
      // The app's page sends owners on to the app with a handoff code and
      // shows everyone else the purchase page.
      return (
        <Redirect to={`/${props.match.params.redirect}/confirm-purchase`} />
      );
    }
    return <Redirect to="/" />;
  }

  return (
    <LoginContainer>
      <div className="login-header">
        <Title>Login to Medzcool</Title>
      </div>

      <div className="login-background">
        <>
          {!error && !cannotAuthMsg && formSubmitted ? (
            <Spinner />
          ) : (
            <FormContainer>
              {cannotAuthMsg && (
                <MediumText className="login-error">
                  <p>{cannotAuthMsg}</p>
                </MediumText>
              )}

              {error && (
                <MediumText className="login-error">
                  <p>{error.graphQLErrors[0].message}</p>
                </MediumText>
              )}

              <form onSubmit={(e) => handleSubmit(e, login)}>
                <input
                  type="text"
                  value={username}
                  placeholder="Username or Email"
                  onChange={handleUsername}
                />
                <input
                  type="password"
                  value={password}
                  placeholder="Password"
                  onChange={handlePassword}
                />
                <Button
                  buttonLabel="Sign-In"
                  active={formFilled}
                  onClick={(e) => handleLogin(e, login)}
                  type="submit"
                />
              </form>
            </FormContainer>
          )}
        </>
      </div>

      <div className="bottom">
        <span className="bottom-items">
          <a href="https://api.medzcool.com/accounts/password_reset">
            Forgot Password?
          </a>
        </span>
        &middot;
        <span className="bottom-items">
          <Link to="/register">Don't have an account?</Link>
        </span>
        &middot;
        <span className="bottom-items">
          <Link to="/privacy">Privacy Policy</Link>
        </span>
      </div>

      <BackgroundImg />
    </LoginContainer>
  );
}

const LoginContainer = styled.div`
  padding: 15px;
  max-width: 500px;
  margin: auto;
  margin-top: 100px;
  .login-header {
    margin-bottom: 25px;
    text-align: center;
  }
  .login-background {
    background: white;
    padding: 25px;
    border-radius: 15px;
    border: 1px solid #e8e8e8;
  }
  .bottom {
    margin-top: 25px;
    text-align: center;
    .bottom-items {
      margin-right: 5px;
      margin-left: 5px;
      a {
        color: black;
        font-size: 12px;
        font-weight: 500;
      }
    }
  }

  ${Title} {
    margin-bottom: 0px;
    font-weight: 500;
  }
  .consent {
    margin-top: 50px;
  }
  a {
    color: #062bba;
  }
  @media (max-width: ${BreakPoints.mobile}) {
    .login-background {
      border: none;
    }
  } ;
`;
const FormContainer = styled.div`
  margin-bottom: 25px;
  input {
    box-sizing: border-box;
    padding: 15px;
    width: 100%;
    border-radius: 3px;
    border: lightgray 1px solid;
    margin-bottom: 15px;
    font-size: 16px;
    font-family: ${fontFamily.sansSerif};
  }
  .login-error {
    background-color: ${yellow.light};
    padding: 8px 15px;
    margin-bottom: 15px;
    border-radius: 5px;
  }
`;
const BackgroundImg = styled.div`
  margin-bottom: 0;
  height: 100vh;
  width: 100vw;
  position: absolute;
  background-size: 600px;
  background-image: url(${backgroundImg});
  background-position: center center;
  background-repeat: repeat;
  background-attachment: fixed;
  top: 0;
  left: 0;
  z-index: -1;
  @media (max-width: ${BreakPoints.mobile}) {
    display: none;
  } ;
`;
